import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Download, ExternalLink, ImageOff, Link2, Loader2, Maximize2, Minimize2, X, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { copyFileLink } from "@/lib/files";
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog";
import { clampPan, clampScale, IDENTITY, swipeIntent, toggleZoom, wheelFactor, wrapIndex, zoomAt, type ZoomState } from "@/lib/image-zoom";
import { cn } from "@/lib/utils";

/** A toolbar button's look, for `ViewerImage.actions` to match. */
export const VIEWER_ICON_BUTTON =
  "grid size-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** One picture the viewer can show. `src` may be a blob URL (a staged upload). */
export interface ViewerImage {
  src: string;
  filename: string;
  alt?: string;
  /** A line under the picture: the chat message a photo came in, say. */
  caption?: string;
  /** The picture's link: given, the toolbar has Copy link. */
  link?: string | null;
  /** More toolbar buttons for this picture (Make private, say). */
  actions?: React.ReactNode;
}

/**
 * A set of sibling images and the viewer they open in. `children` draws the
 * thumbnails and gets `open(index)`; the viewer pages through the whole set, so
 * a comment's second screenshot is one swipe from its first.
 */
export function ImageGroup({
  images,
  onIndexChange,
  className,
  children,
}: {
  images: ViewerImage[];
  onIndexChange?: (index: number) => void;
  /** Given, the thumbnails are wrapped in a `div` carrying it. */
  className?: string;
  children: (open: (index: number) => void) => React.ReactNode;
}) {
  const { open, viewer } = useImageViewer(images, onIndexChange);
  if (className === undefined) {
    return (
      <>
        {children(open)}
        {viewer}
      </>
    );
  }
  return (
    <div className={className}>
      {children(open)}
      {viewer}
    </div>
  );
}

/**
 * The same as `ImageGroup` for a component that can hold a hook: `open(index)`
 * for the thumbnails, and `viewer` to render anywhere (it portals out).
 */
export function useImageViewer(images: ViewerImage[], onIndexChange?: (index: number) => void) {
  const [open, setOpen] = useState(false);
  // Kept apart from `open` so the exit animation still shows the picture that was open.
  const [index, setIndex] = useState(0);
  const show = useCallback((at: number) => {
    setIndex(at);
    setOpen(true);
  }, []);
  const viewer = (
    <ImageViewer
      images={images}
      index={index}
      open={open}
      onOpenChange={setOpen}
      onIndexChange={(next) => {
        setIndex(next);
        onIndexChange?.(next);
      }}
    />
  );
  return { open: show, viewer };
}

/**
 * The in-app image viewer: a popup over a dimmed page with the picture fitted to
 * the screen, paging, zoom and pan by hand, and a full-screen mode. Built on the
 * `Dialog` root, so the system back closes it like any other overlay.
 */
export function ImageViewer({
  images,
  index,
  open,
  onOpenChange,
  onIndexChange,
}: {
  images: ViewerImage[];
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIndexChange: (index: number) => void;
}) {
  const count = images.length;
  const at = count ? Math.min(Math.max(index, 0), count - 1) : 0;
  const image = images[at];
  return (
    <Dialog open={open && count > 0} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-black/75 backdrop-blur-sm" />
        {image ? (
          <ViewerBody
            image={image}
            index={at}
            count={count}
            neighbours={count > 1 ? [images[wrapIndex(at - 1, count)]!.src, images[wrapIndex(at + 1, count)]!.src] : []}
            onClose={() => onOpenChange(false)}
            onPage={(step) => onIndexChange(wrapIndex(at + step, count))}
          />
        ) : null}
      </DialogPortal>
    </Dialog>
  );
}

type FullMode = "native" | "fallback" | null;
type LoadState = "loading" | "loaded" | "error";

interface Gesture {
  pointers: Map<number, { x: number; y: number }>;
  type: string;
  startX: number;
  startY: number;
  startAt: number;
  startView: ZoomState;
  onImage: boolean;
  moved: boolean;
  mode: "none" | "pan" | "swipe" | "pinch";
  axis: "x" | "y" | null;
  pinch: { dist: number; midX: number; midY: number; view: ZoomState } | null;
  pinched: boolean;
}

type FullscreenDocument = Document & {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FullscreenElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };

/** Element full screen, where the browser has it: not on an iPhone, which only does it for video. */
function canFullscreen(element: HTMLElement | null): boolean {
  const doc = document as FullscreenDocument;
  const el = element as FullscreenElement | null;
  if (!el) return false;
  if (document.fullscreenEnabled && typeof el.requestFullscreen === "function") return true;
  return Boolean(doc.webkitFullscreenEnabled && typeof el.webkitRequestFullscreen === "function");
}

function fullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

function exitFullscreen() {
  const doc = document as FullscreenDocument;
  try {
    if (document.fullscreenElement && document.exitFullscreen) void document.exitFullscreen().catch(() => {});
    else if (doc.webkitFullscreenElement && doc.webkitExitFullscreen) void doc.webkitExitFullscreen();
  } catch {
    // Leaving full screen is best effort; the browser's own Esc still works.
  }
}

const DOUBLE_TAP_MS = 280;
const MOVE_SLOP = 6;

function ViewerBody({
  image,
  index,
  count,
  neighbours,
  onClose,
  onPage,
}: {
  image: ViewerImage;
  index: number;
  count: number;
  neighbours: string[];
  onClose: () => void;
  onPage: (step: number) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // Whatever had focus when the viewer opened (the thumbnail), captured before Radix moves it.
  const [returnFocus] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null));

  const [view, setView] = useState<ZoomState>(IDENTITY);
  const viewRef = useRef(view);
  viewRef.current = view;
  const [drag, setDrag] = useState<{ dx: number; dy: number } | null>(null);
  const [gesturing, setGesturing] = useState(false);
  const [full, setFull] = useState<FullMode>(null);
  const [chrome, setChrome] = useState(true);
  const [loaded, setLoaded] = useState<{ src: string; state: LoadState }>({ src: image.src, state: "loading" });
  const status: LoadState = loaded.src === image.src ? loaded.state : "loading";

  const gesture = useRef<Gesture | null>(null);
  const tapTimer = useRef<number | undefined>(undefined);
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);

  const many = count > 1;
  const zoomed = view.scale > 1.01;

  const sizes = useCallback(() => {
    const img = imgRef.current;
    const stage = stageRef.current;
    return {
      image: { width: img?.offsetWidth ?? 0, height: img?.offsetHeight ?? 0 },
      stage: { width: stage?.clientWidth ?? 0, height: stage?.clientHeight ?? 0 },
    };
  }, []);

  /** A client point as an offset from the stage's centre. */
  const fromCentre = useCallback((clientX: number, clientY: number) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: clientX - (rect.left + rect.width / 2), y: clientY - (rect.top + rect.height / 2) };
  }, []);

  // A new picture starts fitted, and loading.
  useLayoutEffect(() => {
    setView(IDENTITY);
    setDrag(null);
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded({ src: image.src, state: "loaded" });
  }, [image.src]);

  // Warm the neighbours so paging does not wait on the network.
  useEffect(() => {
    for (const src of neighbours) {
      const warm = new Image();
      warm.src = src;
    }
  }, [neighbours.join("\n")]);

  // The stage changed size (rotation, full screen): a zoom made for the old one no longer fits.
  useEffect(() => {
    const reset = () => setView(IDENTITY);
    window.addEventListener("resize", reset);
    return () => window.removeEventListener("resize", reset);
  }, []);

  // Track the browser's own full screen, which Esc leaves without asking us.
  useEffect(() => {
    const sync = () => {
      const element = fullscreenElement();
      setFull((mode) => (element && element === rootRef.current ? "native" : mode === "native" ? null : mode));
      setView(IDENTITY);
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
      if (rootRef.current && fullscreenElement() === rootRef.current) exitFullscreen();
      window.clearTimeout(tapTimer.current);
    };
  }, []);

  // Controls come back whenever full screen is left.
  useEffect(() => {
    if (!full) setChrome(true);
  }, [full]);

  // The wheel zooms at the cursor. Attached by hand: React's wheel listener is passive
  // and could not stop the page behind from scrolling.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const point = fromCentre(event.clientX, event.clientY);
      const { image: img, stage: box } = sizes();
      setView((current) => zoomAt(current, current.scale * wheelFactor(event.deltaY, event.deltaMode), point, img, box));
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [fromCentre, sizes]);

  const toggleFull = useCallback(() => {
    if (full === "native") return exitFullscreen();
    if (full === "fallback") return setFull(null);
    const el = rootRef.current as FullscreenElement | null;
    if (el && canFullscreen(el)) {
      try {
        const request = el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen?.();
        // A refusal (no user activation, a policy) still gets the edge-to-edge overlay.
        if (request && typeof (request as Promise<void>).catch === "function") (request as Promise<void>).catch(() => setFull("fallback"));
      } catch {
        setFull("fallback");
      }
      return;
    }
    setFull("fallback");
  }, [full]);

  const zoomBy = (step: 1 | -1 | 0) => {
    const { image: img, stage: box } = sizes();
    setView((current) =>
      step === 0 ? toggleZoom(current, { x: 0, y: 0 }, img, box) : zoomAt(current, current.scale * (step > 0 ? 1.5 : 1 / 1.5), { x: 0, y: 0 }, img, box),
    );
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const key = event.key;
    if (key === "ArrowLeft" && many) onPage(-1);
    else if (key === "ArrowRight" && many) onPage(1);
    else if (key === "+" || key === "=") zoomBy(1);
    else if (key === "-" || key === "_") zoomBy(-1);
    else if (key === "0") setView(IDENTITY);
    else if (key === "f" || key === "F") toggleFull();
    else return;
    event.preventDefault();
  };

  // ── Pointer gestures: pan, pinch, swipe, tap and double-tap, all on the stage ──

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const stage = event.currentTarget;
    stage.setPointerCapture?.(event.pointerId);
    const current = gesture.current;
    if (current && current.pointers.size > 0) {
      current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (current.pointers.size === 2) {
        const [a, b] = [...current.pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
        const mid = fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
        current.mode = "pinch";
        current.moved = true;
        current.pinched = true;
        current.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, midX: mid.x, midY: mid.y, view: viewRef.current };
        setDrag(null);
      }
      return;
    }
    gesture.current = {
      pointers: new Map([[event.pointerId, { x: event.clientX, y: event.clientY }]]),
      type: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      startAt: performance.now(),
      startView: viewRef.current,
      onImage: event.target === imgRef.current,
      moved: false,
      mode: "none",
      axis: null,
      pinch: null,
      pinched: false,
    };
    setGesturing(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    if (!current || !current.pointers.has(event.pointerId)) return;
    current.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const { image: img, stage: box } = sizes();

    if (current.mode === "pinch" && current.pinch && current.pointers.size >= 2) {
      const [a, b] = [...current.pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const mid = fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
      const { dist, midX, midY, view: from } = current.pinch;
      const scale = from.scale * (Math.hypot(a.x - b.x, a.y - b.y) / dist);
      // The image point under the fingers' first midpoint follows the midpoint as it moves.
      const ratio = clampScale(scale) / from.scale;
      setView(clampPan({ scale, x: mid.x - (midX - from.x) * ratio, y: mid.y - (midY - from.y) * ratio }, img, box));
      return;
    }

    const dx = event.clientX - current.startX;
    const dy = event.clientY - current.startY;
    if (!current.moved) {
      if (Math.hypot(dx, dy) < MOVE_SLOP) return;
      current.moved = true;
      if (current.startView.scale > 1.01) current.mode = "pan";
      else if (current.type !== "mouse") {
        current.mode = "swipe";
        current.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      }
    }
    if (current.mode === "pan") {
      setView(clampPan({ scale: current.startView.scale, x: current.startView.x + dx, y: current.startView.y + dy }, img, box));
    } else if (current.mode === "swipe") {
      // Sideways pages (and only drags when there is somewhere to go); down closes.
      setDrag(current.axis === "x" ? { dx: many ? dx : dx / 4, dy: 0 } : { dx: 0, dy: Math.max(0, dy) + Math.min(0, dy) / 4 });
    }
  };

  const onPointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    if (!current || !current.pointers.has(event.pointerId)) return;
    current.pointers.delete(event.pointerId);

    if (current.pointers.size > 0) {
      // One finger of a pinch lifted: carry on as a pan from where the other one is.
      const [rest] = [...current.pointers.values()] as [{ x: number; y: number }];
      current.mode = "pan";
      current.pinch = null;
      current.startX = rest.x;
      current.startY = rest.y;
      current.startView = viewRef.current;
      return;
    }

    gesture.current = null;
    setGesturing(false);
    if (event.type === "pointercancel") {
      setDrag(null);
      return;
    }

    if (current.pinched) {
      if (viewRef.current.scale < 1.05) setView(IDENTITY);
      return;
    }

    if (current.mode === "swipe") {
      const dx = event.clientX - current.startX;
      const dy = event.clientY - current.startY;
      const intent = swipeIntent(current.axis === "x" ? dx : 0, current.axis === "y" ? dy : 0, performance.now() - current.startAt, many);
      setDrag(null);
      if (intent === "next") onPage(1);
      else if (intent === "prev") onPage(-1);
      else if (intent === "close") onClose();
      return;
    }

    if (!current.moved) onTap(event.clientX, event.clientY, current.onImage, current.type);
  };

  const onTap = (clientX: number, clientY: number, onImage: boolean, type: string) => {
    const point = fromCentre(clientX, clientY);
    if (type === "mouse") {
      // A click on the picture zooms there (or back out); a click beside it closes.
      if (onImage || zoomed) {
        const { image: img, stage: box } = sizes();
        setView((current) => toggleZoom(current, point, img, box));
      } else onClose();
      return;
    }
    const now = performance.now();
    const previous = lastTap.current;
    if (previous && now - previous.at < DOUBLE_TAP_MS && Math.hypot(clientX - previous.x, clientY - previous.y) < 30) {
      window.clearTimeout(tapTimer.current);
      lastTap.current = null;
      const { image: img, stage: box } = sizes();
      setView((current) => toggleZoom(current, point, img, box));
      return;
    }
    lastTap.current = { at: now, x: clientX, y: clientY };
    window.clearTimeout(tapTimer.current);
    tapTimer.current = window.setTimeout(() => {
      lastTap.current = null;
      // A single tap: beside the picture closes, on it shows or hides the controls in full screen.
      if (!onImage && !zoomed) onClose();
      else if (full) setChrome((shown) => !shown);
    }, DOUBLE_TAP_MS);
  };

  const dx = drag?.dx ?? 0;
  const dy = drag?.dy ?? 0;
  const fade = drag ? Math.max(0.35, 1 - dy / 450) : 1;
  const chromeHidden = Boolean(full) && !chrome;
  const chromeClass = cn(
    "transition-opacity duration-200 motion-reduce:transition-none",
    chromeHidden && "pointer-events-none opacity-0",
  );
  const pill = "border border-border/70 bg-elevated/90 text-foreground shadow-lg backdrop-blur";
  const iconButton = VIEWER_ICON_BUTTON;

  return (
    <DialogPrimitive.Content
      ref={rootRef}
      aria-describedby={undefined}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        rootRef.current?.focus({ preventScroll: true });
      }}
      onCloseAutoFocus={(event) => {
        // Back to the thumbnail without scrolling the page under it.
        event.preventDefault();
        returnFocus?.focus({ preventScroll: true });
      }}
      onEscapeKeyDown={(event) => {
        // Esc leaves the edge-to-edge overlay first, as it does the browser's own full screen.
        if (full === "fallback") {
          event.preventDefault();
          setFull(null);
        }
      }}
      onKeyDown={onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className={cn(
        "fixed inset-x-0 top-0 bottom-[var(--kb,0px)] z-50 flex select-none flex-col outline-none",
        "motion-dialog",
        full
          ? // Edge to edge: the picture under the notch, the controls inside the safe area.
            "bg-black"
          : "gap-2 px-[max(env(safe-area-inset-left),0.5rem)] pb-[max(env(safe-area-inset-bottom),0.5rem)] pt-[max(env(safe-area-inset-top),0.5rem)] sm:gap-3 sm:px-6 sm:pb-5 sm:pt-5",
      )}
      data-full={full ?? undefined}
    >
      {/* Toolbar */}
      <div
        className={cn(
          "z-10 flex items-center gap-1 rounded-lg p-1 pl-3",
          pill,
          full &&
            "absolute inset-x-[max(env(safe-area-inset-left),0.5rem)] top-[max(env(safe-area-inset-top),0.5rem)] sm:inset-x-6 sm:top-5",
          chromeClass,
        )}
      >
        <DialogPrimitive.Title className="min-w-0 flex-1 truncate text-[13px] font-medium" title={image.filename}>
          {image.filename}
        </DialogPrimitive.Title>
        <button type="button" className={cn(iconButton, "max-sm:hidden")} onClick={() => zoomBy(0)} aria-label={zoomed ? "Zoom out" : "Zoom in"} title={zoomed ? "Zoom out (0)" : "Zoom in (+)"}>
          {zoomed ? <ZoomOut className="size-4" /> : <ZoomIn className="size-4" />}
        </button>
        {image.actions}
        {image.link ? (
          <button type="button" className={iconButton} onClick={() => copyFileLink(image.link!, toast)} aria-label="Copy link" title="Copy link">
            <Link2 className="size-4" />
          </button>
        ) : null}
        <a href={image.src} target="_blank" rel="noreferrer" className={iconButton} aria-label="Open original in a new tab" title="Open original">
          <ExternalLink className="size-4" />
        </a>
        <a href={image.src} download={image.filename || true} className={iconButton} aria-label="Download" title="Download">
          <Download className="size-4" />
        </a>
        <button type="button" className={iconButton} onClick={toggleFull} aria-label={full ? "Exit full screen" : "Full screen"} title={full ? "Exit full screen (F)" : "Full screen (F)"}>
          {full ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </button>
        <DialogPrimitive.Close className={iconButton} aria-label="Close" title="Close (Esc)">
          <X className="size-4" />
        </DialogPrimitive.Close>
      </div>

      {/* Stage: the picture, and the arrows beside it (siblings, so a press on one is no gesture). */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={stageRef}
          className={cn(
            "absolute inset-0 flex touch-none items-center justify-center overflow-hidden",
            // Room beside the picture for the arrows, where there is room to spare.
            many && !full && "sm:inset-x-14",
            zoomed ? (gesturing ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in",
            !zoomed && status !== "loaded" && "cursor-default",
          )}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onDoubleClick={(event) => event.preventDefault()}
        >
          <img
            ref={imgRef}
            key={image.src}
            src={image.src}
            alt={image.alt ?? image.filename}
            draggable={false}
            onLoad={() => setLoaded({ src: image.src, state: "loaded" })}
            onError={() => setLoaded({ src: image.src, state: "error" })}
            className={cn(
              "max-h-full max-w-full object-contain will-change-transform",
              !full && "rounded-md shadow-2xl",
              gesturing ? "" : "transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none",
              status === "loaded" ? "" : "opacity-0",
            )}
            style={{
              transform: `translate3d(${view.x + dx}px, ${view.y + dy}px, 0) scale(${view.scale})`,
              opacity: status === "loaded" ? fade : undefined,
            }}
          />
          {status === "loading" ? (
            <span className={cn("pointer-events-none absolute grid size-11 place-items-center rounded-full", pill)} role="status" aria-label="Loading image">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </span>
          ) : status === "error" ? (
            <div
              className={cn("absolute flex max-w-xs flex-col items-center gap-2 rounded-lg px-5 py-4 text-center", pill)}
              role="alert"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <ImageOff className="size-6 text-muted-foreground" />
              <p className="text-[13px] font-medium">This image could not be loaded</p>
              <p className="text-[12px] text-muted-foreground">It may have been deleted, or the connection dropped.</p>
              <a href={image.src} target="_blank" rel="noreferrer" className="text-[12px] font-medium text-primary underline underline-offset-2">
                Try the original
              </a>
            </div>
          ) : null}
        </div>

        {many ? (
          <>
            <button
              type="button"
              onClick={() => onPage(-1)}
              aria-label="Previous image"
              title="Previous (←)"
              className={cn("absolute left-1 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full sm:left-1.5", pill, "hover:bg-muted", chromeClass)}
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => onPage(1)}
              aria-label="Next image"
              title="Next (→)"
              className={cn("absolute right-1 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-full sm:right-1.5", pill, "hover:bg-muted", chromeClass)}
            >
              <ChevronRight className="size-5" />
            </button>
          </>
        ) : null}
      </div>

      {/* Footer: where in the set, and what the picture is of. */}
      {many || image.caption ? (
        <div
          className={cn(
            "z-10 mx-auto flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-[12px]",
            pill,
            full && "absolute bottom-[max(env(safe-area-inset-bottom),0.75rem)] left-1/2 -translate-x-1/2",
            chromeClass,
          )}
        >
          {many ? (
            <span className="shrink-0 font-medium tabular-nums" aria-live="polite">
              {index + 1} / {count}
            </span>
          ) : null}
          {image.caption ? <span className="min-w-0 truncate text-muted-foreground">{image.caption}</span> : null}
        </div>
      ) : null}
    </DialogPrimitive.Content>
  );
}
